import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

const EMPTY = { requests: [], maxRequestsPerUser: 0, activeRequestCount: 0 };

/**
 * The signed-in listener's own waiting requests on a band's queue
 * (`GET /bands/{id}/queue/mine`, ENG-684), for the YOUR SONG card.
 *
 * It reads once when enabled (signed in, band live), then again whenever
 * `queueKey` changes (the bootstrap's queue, which follows every
 * queue_update) but only while the listener has requests: a viewer with none
 * doesn't add a request per queue change. `refresh()` reads now, after the
 * listener's own boost or switch.
 */
export const useMyRequests = (bandId, { enabled = true, queueKey } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [data, setData] = useState(EMPTY);
  const dataRef = useRef(data);
  dataRef.current = data;
  const seqRef = useRef(0);
  const holdingRef = useRef(false);

  const load = useCallback(() => {
    if (!bandId || !enabled) return;
    const seq = ++seqRef.current;
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/bands/${bandId}/queue/mine`)
      .then((answer) => {
        if (seq !== seqRef.current) return;
        const next = answer && Array.isArray(answer.requests) ? answer : EMPTY;
        holdingRef.current = next.requests.length > 0;
        setData(next);
      })
      .catch(() => {
        // Keep the last good answer through a failed read.
      });
  }, [bandId, enabled]);

  useEffect(() => {
    if (!enabled) {
      holdingRef.current = false;
      setData(EMPTY);
      return;
    }
    load();
  }, [enabled, load]);

  const firstKey = useRef(true);
  useEffect(() => {
    if (firstKey.current) {
      firstKey.current = false;
      return;
    }
    if (holdingRef.current) load();
  }, [queueKey, load]);

  /*
   * The private request_positions frame (ENG-716/722) after a queue change:
   * the listener's spots move at once, without another read. A request that
   * left the queue leaves the card; one the card has not seen yet (just
   * confirmed) is read in full.
   */
  const applyPositions = useCallback((frame) => {
    if (!frame || !Array.isArray(frame.requests)) return;
    const byEntry = new Map(frame.requests.map((p) => [p.entryId, p]));
    const known = new Set(dataRef.current.requests.map((r) => r.entryId));
    const unknown = frame.requests.some((p) => !known.has(p.entryId));
    setData((prev) => {
      const requests = prev.requests
        .filter((r) => byEntry.has(r.entryId))
        .map((r) => {
          const p = byEntry.get(r.entryId);
          return { ...r, spot: p.spot, songsAway: p.songsAway, locked: p.locked, amountCents: p.amountCents };
        });
      holdingRef.current = requests.length > 0;
      return { ...prev, requests, activeRequestCount: Math.max(0, prev.activeRequestCount - (prev.requests.length - requests.length)) };
    });
    if (unknown) load();
  }, [load]);

  return { ...data, refresh: load, applyPositions };
};
